import type { ShareSummary } from "./types";

export type ShareCardFormat = "og" | "square";

export const SHARE_CARD_SIZES: Record<
  ShareCardFormat,
  { width: number; height: number }
> = {
  og: { width: 1200, height: 630 },
  square: { width: 1080, height: 1080 },
};

export function shareText(summary: ShareSummary): string {
  return `${summary.title} — ${summary.subtitle}`;
}

export function shareFilename(summary: ShareSummary, format: ShareCardFormat) {
  return `${slugify(summary.routeTitle)}-${summary.kind}-${format}.svg`;
}

export function renderShareCardSvg(
  summary: ShareSummary,
  format: ShareCardFormat,
): string {
  const { width, height } = SHARE_CARD_SIZES[format];
  const title = escapeXml(summary.title);
  const subtitle = escapeXml(summary.subtitle);
  const routeTitle = escapeXml(summary.routeTitle);
  const statLine = summary.statLine ? escapeXml(summary.statLine) : "";
  const isSquare = format === "square";
  const titleY = isSquare ? 445 : 280;
  const subtitleY = isSquare ? 540 : 356;
  const statY = isSquare ? 640 : 438;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" role="img" aria-label="${title}">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#020617"/>
      <stop offset="52%" stop-color="#0e7490"/>
      <stop offset="100%" stop-color="#111827"/>
    </linearGradient>
    <radialGradient id="moon" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#f8fafc"/>
      <stop offset="68%" stop-color="#cbd5e1"/>
      <stop offset="100%" stop-color="#64748b"/>
    </radialGradient>
  </defs>
  <rect width="${width}" height="${height}" fill="url(#bg)"/>
  <circle cx="${width - 180}" cy="${isSquare ? 190 : 150}" r="${isSquare ? 132 : 92}" fill="url(#moon)" opacity="0.96"/>
  <circle cx="${width - 214}" cy="${isSquare ? 164 : 130}" r="18" fill="#94a3b8" opacity="0.38"/>
  <circle cx="${width - 134}" cy="${isSquare ? 222 : 170}" r="28" fill="#94a3b8" opacity="0.3"/>
  <path d="M0 ${height - 150} C ${width * 0.28} ${height - 230}, ${width * 0.62} ${height - 50}, ${width} ${height - 150} L ${width} ${height} L 0 ${height} Z" fill="#cffafe" opacity="0.18"/>
  <text x="72" y="${isSquare ? 94 : 82}" fill="#a5f3fc" font-family="Inter, system-ui, sans-serif" font-size="28" font-weight="700" letter-spacing="2">FITNESS RPG</text>
  <text x="72" y="${titleY}" fill="#ffffff" font-family="Inter, system-ui, sans-serif" font-size="${isSquare ? 76 : 68}" font-weight="800">${title}</text>
  <text x="72" y="${subtitleY}" fill="#cffafe" font-family="Inter, system-ui, sans-serif" font-size="${isSquare ? 38 : 34}" font-weight="600">${subtitle}</text>
  ${
    statLine
      ? `<text x="72" y="${statY}" fill="#ffffff" font-family="Inter, system-ui, sans-serif" font-size="${isSquare ? 46 : 40}" font-weight="700">${statLine}</text>`
      : ""
  }
  <text x="72" y="${height - 70}" fill="#e0f2fe" font-family="Inter, system-ui, sans-serif" font-size="28" font-weight="600">${routeTitle}</text>
</svg>`;
}

export function shareCardDataUrl(
  summary: ShareSummary,
  format: ShareCardFormat,
): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(renderShareCardSvg(summary, format))}`;
}

function escapeXml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
